import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

const IRON_PRODUCT_HANDLE = 'elymont-portable-steam-iron';
const IRON_GIFT_VARIANT_ID = 48905317351581;
const STEAM_CLEANER_GIFT_VARIANT_ID = 67645292282178;

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    100
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    this.#syncSteamCleanerGiftOnConnect().catch((error) => {
      console.warn('[cart-items] Steam cleaner gift was not synced on load:', error);
    });

    this.#syncProtectionQuantityOnConnect().catch((error) => {
      console.warn('[cart-items] Shipping protection quantity was not synced on load:', error);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getRowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getRowForLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const isLastVisibleGiftBundleItem = this.#isRemovingLastVisibleGiftBundleItem(rowsToRemove);

    const template = document.getElementById('empty-cart-template');
    if ((isEmptyCart || isLastVisibleGiftBundleItem) && template instanceof HTMLTemplateElement) {
      if (this.#shouldCloseCustomDrawerWhenEmpty()) {
        this.closest('theme-drawer')?.close?.();
        return;
      }

      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  #shouldCloseCustomDrawerWhenEmpty() {
    return (
      this.isDrawer &&
      this.closest('.cart-drawer__inner--elymont-trc, .cart-drawer__inner--iron, .cart-drawer__inner--steam-cleaner, .cart-drawer__inner--work-light') !==
        null
    );
  }

  /**
   * @param {HTMLTableRowElement[]} rowsToRemove
   * @returns {boolean}
   */
  #isRemovingLastVisibleGiftBundleItem(rowsToRemove) {
    if (!this.isDrawer || !this.closest('.cart-drawer__inner--iron, .cart-drawer__inner--steam-cleaner')) return false;

    const removingRows = new Set(rowsToRemove);
    return this.refs.cartItemRows.every(
      (row) =>
        removingRows.has(row) ||
        row.hasAttribute('data-iron-gift-line') ||
        row.hasAttribute('data-steam-cleaner-gift-line')
    );
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    this.#disableCartItems(line);
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const giftBulkUpdates = this.#getGiftBulkUpdates(line, quantity);
    const sections = Array.from(sectionsToUpdate).join(',');
    const body = JSON.stringify(
      giftBulkUpdates
        ? {
            updates: giftBulkUpdates,
            sections,
            sections_url: this.#getSectionsUrl(),
          }
        : {
            line: line,
            quantity: quantity,
            sections,
            sections_url: this.#getSectionsUrl(),
          }
    );

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.#getRowForLine(line)?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(giftBulkUpdates ? Theme.routes.cart_update_url : `${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        const responseText = await response.text();
        let parsedResponseText;

        try {
          parsedResponseText = JSON.parse(responseText);
        } catch {
          throw new Error(`Cart update returned non-JSON response: ${response.status}`);
        }

        if (!response.ok) {
          throw new Error(parsedResponseText.errors || parsedResponseText.message || `Cart update failed: ${response.status}`);
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        parsedResponseText = await this.#syncProtectionQuantity(parsedResponseText);
        parsedResponseText = await this.#syncIronGiftQuantity(parsedResponseText);
        parsedResponseText = await this.#syncSteamCleanerGiftQuantity(parsedResponseText);
        parsedResponseText = await this.#removeProtectionIfOnlyHiddenItem(parsedResponseText);

        parsedResponseText.sections = await this.#ensureRenderableSection(parsedResponseText.sections);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(async (error) => {
        console.warn('[cart-items] Cart update failed:', error);
        await this.#restoreCartSectionAfterFailedUpdate().catch((restoreError) => {
          console.warn('[cart-items] Failed to restore cart section:', restoreError);
        });

        deferredUpdatePromise.resolve({
          cart: {
            totalQuantity: this.#getCurrentCartItemCount(),
          },
          detail: {
            sections: {},
            items: [],
            itemCount: this.#getCurrentCartItemCount(),
            source: 'cart-items-component',
            didError: true,
          },
        });

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelectorForLine(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              if (!this.#replaceCartDrawerInner(cartItemsHtml)) {
                morphSection(this.sectionId, cartItemsHtml, morphOptions);
              }
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems(line) {
    this.classList.add('cart-items-disabled');
    this.#getRowsForLine(line).forEach((row) => row.classList.add('cart-items__table-row--loading'));
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.querySelectorAll('.cart-items__table-row--loading').forEach((row) => {
      row.classList.remove('cart-items__table-row--loading');
    });
  }

  /**
   * @param {number} line
   * @returns {HTMLTableRowElement[]}
   */
  #getRowsForLine(line) {
    const lineItemRow = this.#getRowForLine(line);
    if (!lineItemRow) return [];

    return [
      lineItemRow,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === lineItemRow.dataset.key),
    ];
  }

  /**
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getRowForLine(line) {
    return this.refs.cartItemRows.find((row) => Number(row.dataset.line) === line);
  }

  /**
   * @param {number} line
   * @returns {HTMLElement | undefined}
   */
  #getQuantitySelectorForLine(line) {
    return this.refs.quantitySelectors.find((selector) => {
      const input = selector.querySelector('input[data-cart-line]');
      return Number(input?.getAttribute('data-cart-line')) === line;
    });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Shopify can occasionally return an empty section string from cart Ajax while the
   * cart mutation itself succeeds. Refetch the section so the cart UI does not get
   * stuck with stale or empty markup.
   * @param {Record<string, string> | undefined} sections
   * @returns {Promise<Record<string, string>>}
   */
  async #ensureRenderableSection(sections) {
    if (sections?.[this.sectionId]?.trim()) return sections;

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, new URL(window.location.href));
      return { ...(sections || {}), [this.sectionId]: html };
    } catch (error) {
      console.warn('[cart-items] Cart section was not refreshed:', error);
      return sections || {};
    }
  }

  #getSectionsUrl() {
    return `${window.location.pathname}${window.location.search}`;
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @returns {Record<string, number> | null}
   */
  #getGiftBulkUpdates(line, quantity) {
    return this.#getSteamCleanerBulkUpdates(line, quantity) || this.#getIronBulkUpdates(line, quantity);
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @returns {Record<string, number> | null}
   */
  #getSteamCleanerBulkUpdates(line, quantity) {
    const row = this.#getRowForLine(line);
    if (!this.closest('.cart-drawer__inner--steam-cleaner') && !row?.hasAttribute('data-steam-cleaner-main-line')) {
      return null;
    }
    if (!row?.hasAttribute('data-steam-cleaner-main-line') || !row.dataset.key) return null;

    /** @type {Record<string, number>} */
    const updates = {
      [row.dataset.key]: quantity,
    };
    const giftRow = this.querySelector('[data-steam-cleaner-gift-line]');
    const nextGiftQuantity = this.refs.cartItemRows.reduce((sum, steamRow) => {
      if (!steamRow.hasAttribute('data-steam-cleaner-main-line')) return sum;
      const rowQuantity = steamRow === row
        ? quantity
        : Number(steamRow.querySelector('input[data-cart-line]')?.value ?? steamRow.querySelector('input[data-cart-line]')?.getAttribute('value') ?? 0);
      const bundleUnits = Math.max(1, Number(steamRow.dataset.steamCleanerBundleUnits || 1));
      return sum + (Number.isFinite(rowQuantity) ? rowQuantity * bundleUnits : 0);
    }, 0);

    if (giftRow instanceof HTMLElement && giftRow.dataset.key) {
      updates[giftRow.dataset.key] = nextGiftQuantity;
    } else {
      updates[STEAM_CLEANER_GIFT_VARIANT_ID] = nextGiftQuantity;
    }

    return updates;
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @returns {Record<string, number> | null}
   */
  #getIronBulkUpdates(line, quantity) {
    const row = this.#getRowForLine(line);
    if (!this.closest('.cart-drawer__inner--iron') && !row?.hasAttribute('data-iron-main-line')) return null;
    if (!row?.hasAttribute('data-iron-main-line') || !row.dataset.key) return null;

    /** @type {Record<string, number>} */
    const updates = {
      [row.dataset.key]: quantity,
    };
    const giftRow = this.querySelector('[data-iron-gift-line]');
    const nextIronQuantity = this.refs.cartItemRows.reduce((sum, ironRow) => {
      if (!ironRow.hasAttribute('data-iron-main-line')) return sum;
      if (ironRow === row) return sum + quantity;

      const input = ironRow.querySelector('input[data-cart-line]');
      const rowQuantity = Number(input?.value ?? input?.getAttribute('value') ?? 0);
      return sum + (Number.isFinite(rowQuantity) ? rowQuantity : 0);
    }, 0);

    if (giftRow instanceof HTMLElement && giftRow.dataset.key) {
      updates[giftRow.dataset.key] = nextIronQuantity;
    } else {
      updates[IRON_GIFT_VARIANT_ID] = nextIronQuantity;
    }

    return updates;
  }

  /**
   * @param {any} cartResponse
   * @returns {Promise<any>}
   */
  async #syncProtectionQuantity(cartResponse) {
    const protectionVariantId = this.#getProtectionVariantId(cartResponse.sections?.[this.sectionId]);
    if (!protectionVariantId || !Array.isArray(cartResponse.items)) return cartResponse;

    const protectionLineIndex = cartResponse.items.findIndex((item) => Number(item.variant_id) === protectionVariantId);
    if (protectionLineIndex < 0) return cartResponse;

    const protectionItem = cartResponse.items[protectionLineIndex];
    if (Number(protectionItem.quantity || 0) <= 1) return cartResponse;

    const body = JSON.stringify({
      line: protectionLineIndex + 1,
      quantity: 1,
      sections: this.sectionId,
      sections_url: this.#getSectionsUrl(),
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const result = await response.json();
      return response.ok && !result.errors ? result : cartResponse;
    } catch (error) {
      console.warn('[cart-items] Shipping protection quantity was not synced:', error);
      return cartResponse;
    }
  }

  async #syncProtectionQuantityOnConnect() {
    if (!this.isDrawer && !this.querySelector('[data-comb-protection]')) return;

    const cartResponse = await this.fetchCartData();
    const syncedCart = await this.#syncProtectionQuantity(cartResponse);
    const sectionHtml = syncedCart?.sections?.[this.sectionId];
    if (!sectionHtml) return;

    await morphSection(this.sectionId, sectionHtml, {
      mode: this.isDrawer ? 'hydration' : 'full',
    });
    this.#updateCartQuantitySelectorButtonStates();
  }

  async #removeProtectionIfOnlyHiddenItem(cartResponse) {
    const protectionVariantId = this.#getProtectionVariantId(cartResponse.sections?.[this.sectionId]);
    if (!protectionVariantId || !Array.isArray(cartResponse.items)) return cartResponse;

    const visibleItems = cartResponse.items.filter((item) => {
      const variantId = Number(item.variant_id);
      return variantId !== protectionVariantId && variantId !== IRON_GIFT_VARIANT_ID && variantId !== STEAM_CLEANER_GIFT_VARIANT_ID;
    });
    if (visibleItems.length > 0) return cartResponse;

    const protectionLine = cartResponse.items.findIndex((item) => Number(item.variant_id) === protectionVariantId) + 1;
    if (protectionLine <= 0) return cartResponse;

    const body = JSON.stringify({
      line: protectionLine,
      quantity: 0,
      sections: this.sectionId,
      sections_url: this.#getSectionsUrl(),
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const result = await response.json();
      return response.ok && !result.errors ? result : cartResponse;
    } catch (error) {
      console.warn('[cart-items] Hidden protection was not removed:', error);
      return cartResponse;
    }
  }

  /**
   * Keeps the real iron gift line matched to the total iron product quantity.
   * @param {any} cartResponse
   * @returns {Promise<any>}
   */
  async #syncIronGiftQuantity(cartResponse) {
    if (!Array.isArray(cartResponse.items)) return cartResponse;
    const hasIronGift = cartResponse.items.some((item) => Number(item.variant_id) === IRON_GIFT_VARIANT_ID);
    const hasIronMain = cartResponse.items.some((item) => item?.handle === IRON_PRODUCT_HANDLE);
    if (!hasIronGift || (!hasIronMain && !this.querySelector('[data-iron-main-line]'))) return cartResponse;

    const ironQuantity = cartResponse.items.reduce((sum, item) => {
      if (item?.handle !== IRON_PRODUCT_HANDLE) return sum;
      return sum + Number(item.quantity || 0);
    }, 0);
    const giftLineIndex = cartResponse.items.findIndex((item) => Number(item.variant_id) === IRON_GIFT_VARIANT_ID);
    if (giftLineIndex < 0) return cartResponse;

    const giftItem = cartResponse.items[giftLineIndex];
    if (Number(giftItem.quantity || 0) === ironQuantity) return cartResponse;

    const body = JSON.stringify({
      line: giftLineIndex + 1,
      quantity: ironQuantity,
      sections: this.sectionId,
      sections_url: this.#getSectionsUrl(),
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const result = await response.json();
      return response.ok && !result.errors ? result : cartResponse;
    } catch (error) {
      console.warn('[cart-items] Iron gift quantity was not synced:', error);
      return cartResponse;
    }
  }

  /**
   * Keeps the steam-cleaner gift line matched to package units across all steam-cleaner main lines.
   * @param {any} cartResponse
   * @returns {Promise<any>}
   */
  async #syncSteamCleanerGiftQuantity(cartResponse) {
    if (!Array.isArray(cartResponse.items)) return cartResponse;

    const hasSteamContext =
      this.closest('.cart-drawer__inner--steam-cleaner') ||
      this.querySelector('[data-steam-cleaner-main-line]') ||
      cartResponse.items.some((item) => item?.properties?._steam_cleaner_main === 'true' || item?.properties?._steam_cleaner_gift === 'true');
    if (!hasSteamContext) return cartResponse;

    const steamGiftQuantity = cartResponse.items.reduce((sum, item) => {
      if (Number(item.variant_id) === STEAM_CLEANER_GIFT_VARIANT_ID || item?.properties?._steam_cleaner_gift === 'true') {
        return sum;
      }
      const isSteamMain =
        item?.properties?._steam_cleaner_main === 'true' ||
        String(item?.handle || '').includes('steam-cleaner') ||
        String(item?.product_title || item?.title || '').toLowerCase().includes('steam cleaner');
      if (!isSteamMain) return sum;

      const bundleUnits = this.#getSteamCleanerBundleUnits(item);
      return sum + Number(item.quantity || 0) * bundleUnits;
    }, 0);

    const giftLineIndex = cartResponse.items.findIndex(
      (item) => Number(item.variant_id) === STEAM_CLEANER_GIFT_VARIANT_ID || item?.properties?._steam_cleaner_gift === 'true'
    );

    if (giftLineIndex < 0) {
      if (steamGiftQuantity <= 0) return cartResponse;

      const body = JSON.stringify({
        items: [
          {
            id: STEAM_CLEANER_GIFT_VARIANT_ID,
            quantity: steamGiftQuantity,
            properties: {
              _steam_cleaner_gift: 'true',
            },
          },
        ],
        sections: this.sectionId,
        sections_url: this.#getSectionsUrl(),
      });

      try {
        const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
        const result = await response.json();
        return response.ok && !result.errors ? result : cartResponse;
      } catch (error) {
        console.warn('[cart-items] Steam cleaner gift quantity was not added:', error);
        return cartResponse;
      }
    }

    const giftItem = cartResponse.items[giftLineIndex];
    if (Number(giftItem.quantity || 0) === steamGiftQuantity) return cartResponse;

    const body = JSON.stringify({
      line: giftLineIndex + 1,
      quantity: steamGiftQuantity,
      sections: this.sectionId,
      sections_url: this.#getSectionsUrl(),
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const result = await response.json();
      return response.ok && !result.errors ? result : cartResponse;
    } catch (error) {
      console.warn('[cart-items] Steam cleaner gift quantity was not synced:', error);
      return cartResponse;
    }
  }

  async #syncSteamCleanerGiftOnConnect() {
    const hasSteamContext =
      this.closest('.cart-drawer__inner--steam-cleaner') ||
      this.querySelector('[data-steam-cleaner-main-line]') ||
      this.querySelector('[data-steam-cleaner-gift-line]');
    if (!hasSteamContext) return;

    const cartResponse = await this.fetchCartData();
    const syncedCart = await this.#syncSteamCleanerGiftQuantity(cartResponse);
    const sectionHtml = syncedCart?.sections?.[this.sectionId];
    if (!sectionHtml) return;

    await morphSection(this.sectionId, sectionHtml, {
      mode: this.isDrawer ? 'hydration' : 'full',
    });
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * @param {any} item
   * @returns {number}
   */
  #getSteamCleanerBundleUnits(item) {
    const explicitUnits = Number(item?.properties?._steam_cleaner_bundle_units || 0);
    if (Number.isFinite(explicitUnits) && explicitUnits > 0) return explicitUnits;

    const text = `${item?.variant_title || ''} ${item?.title || ''}`;
    if (text.includes('3')) return 3;
    if (text.includes('2')) return 2;
    return 1;
  }

  /**
   * @param {string | undefined} sectionHtml
   * @returns {number}
   */
  #getProtectionVariantId(sectionHtml) {
    const root = sectionHtml ? new DOMParser().parseFromString(sectionHtml, 'text/html') : document;
    const protection = root.querySelector('[data-comb-protection]') || document.querySelector('[data-comb-protection]');
    if (!(protection instanceof HTMLElement)) return 0;

    return Number(protection.dataset.variantId || 0);
  }

  /**
   * Replaces the whole drawer inner when moving from empty cart markup to a filled cart.
   * Hydration morphing can leave the old empty inner in place because the children are
   * structurally different.
   * @param {string} sectionHtml
   * @returns {boolean}
   */
  #replaceCartDrawerInner(sectionHtml) {
    const nextDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextInner = nextDocument.querySelector('#cart-drawer [data-hydration-key="cart-drawer-inner"]');
    const currentInner = document.querySelector('#cart-drawer [data-hydration-key="cart-drawer-inner"]');

    if (!nextInner || !currentInner) return false;

    currentInner.replaceWith(nextInner);
    return true;
  }

  async #restoreCartSectionAfterFailedUpdate() {
    await sectionRenderer.renderSection(this.sectionId, {
      cache: false,
      mode: this.isDrawer ? 'hydration' : 'full',
      injectStylesheet: this.isDrawer,
    });
    this.#updateCartQuantitySelectorButtonStates();
  }

  #getCurrentCartItemCount() {
    const count = Number(this.querySelector('[ref="cartItemCount"]')?.textContent);
    return Number.isFinite(count) ? count : 0;
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
